/**
 * MultiSelectTable Component
 *
 * A reusable multi-select table with TanStack Table + optional virtualization.
 * Supports checkbox selection, pin-selected, infinite scroll, and sorting.
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/utils/classname';

export interface MultiSelectTableProps<TData extends { key: string }> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  selectedKeys: Set<string>;
  onSelectionChange: (keys: Set<string>) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  defaultSort?: SortingState;
  maxRows?: number;
  virtualizationThreshold?: number;
  emptyMessage?: string;
  rowHeight?: number;
  pinSelected?: boolean;
}

/**
 * Table header height (sticky header inside scroll container).
 * Accounts for py-2 (16px) + text-xs (12px) + line-height (~4px).
 */
const TABLE_HEADER_HEIGHT = 32;

/**
 * Distance from bottom of scroll container to trigger onLoadMore.
 */
const SCROLL_THRESHOLD = 100;

/**
 * MultiSelectTable renders a sortable, multi-select table with optional
 * virtualization for large datasets. Modelled after SelectionTable.
 *
 * @example
 * ```tsx
 * <MultiSelectTable
 *   data={items}
 *   columns={getTargetColumns('port')}
 *   selectedKeys={selectedSet}
 *   onSelectionChange={handleSelectionChange}
 *   maxRows={8}
 *   pinSelected
 * />
 * ```
 */
export function MultiSelectTable<TData extends { key: string }>({
  data,
  columns,
  selectedKeys,
  onSelectionChange,
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  defaultSort = [],
  maxRows = 8,
  virtualizationThreshold = 50,
  emptyMessage = 'No results found',
  rowHeight = 40,
  pinSelected = false,
}: MultiSelectTableProps<TData>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(defaultSort);

  const calculatedHeight = maxRows * rowHeight + TABLE_HEADER_HEIGHT;

  // When pinSelected is active and no sort is applied, selected rows float to top.
  // pinnedKeysRef captures selectedKeys at the time data changes so that the order
  // stays stable while the user clicks checkboxes (avoids virtualizer scroll jumps).
  const pinnedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (pinSelected && sorting.length === 0) {
      pinnedKeysRef.current = new Set(selectedKeys);
    }
    // We intentionally only re-capture when data or pinSelected changes, NOT on
    // every selectedKeys change, so the eslint disable is deliberate here.
  }, [data, pinSelected]);

  const sortedData = useMemo(() => {
    if (!pinSelected || sorting.length > 0) return data;
    const pinned = pinnedKeysRef.current;
    return [
      ...data.filter(item => pinned.has(item.key)),
      ...data.filter(item => !pinned.has(item.key)),
    ];
  }, [data, pinSelected, sorting]);

  // Build the checkbox column to prepend
  const checkboxColumn: ColumnDef<TData, unknown> = useMemo(
    () => ({
      id: '__select__',
      size: 40,
      enableSorting: false,
      header: () => {
        const allSelected =
          sortedData.length > 0 &&
          sortedData.every(row => selectedKeys.has(row.key));
        const someSelected =
          sortedData.length > 0 &&
          sortedData.some(row => selectedKeys.has(row.key));
        const isDisabled = hasNextPage === true;
        return (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={
                allSelected ? true : someSelected ? 'indeterminate' : false
              }
              // selectedKeys is a controlled prop that may legitimately contain
              // keys outside the current data (a consumer-side search narrows
              // data while the selection persists). So the bulk toggle must
              // union/subtract the visible keys, never replace or blanket-clear,
              // or off-view selections are silently dropped.
              onCheckedChange={() => {
                if (isDisabled) return;
                const next = new Set(selectedKeys);
                if (allSelected) {
                  // Deselect only the visible rows; preserve off-view selections.
                  sortedData.forEach(row => next.delete(row.key));
                } else {
                  // Add the visible rows to (not replace) the existing selection.
                  sortedData.forEach(row => next.add(row.key));
                }
                onSelectionChange(next);
              }}
              aria-label={
                isDisabled ? 'Load all data to select all' : 'Select all rows'
              }
              disabled={isDisabled}
              title={isDisabled ? 'Load all data to use select all' : undefined}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const isSelected = selectedKeys.has(row.original.key);
        return (
          <div
            className="flex items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => {
                const newKeys = new Set(selectedKeys);
                if (isSelected) {
                  newKeys.delete(row.original.key);
                } else {
                  newKeys.add(row.original.key);
                }
                onSelectionChange(newKeys);
              }}
              aria-label={`Select row ${row.original.key}`}
            />
          </div>
        );
      },
    }),
    [sortedData, selectedKeys, onSelectionChange, hasNextPage]
  );

  const allColumns = useMemo(
    () => [checkboxColumn, ...columns],
    [checkboxColumn, columns]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: sortedData,
    columns: allColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => row.key,
  });

  const { rows } = table.getRowModel();

  const useVirtualization = data.length > virtualizationThreshold;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
    enabled: useVirtualization,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const hasPaginationSupport = onLoadMore !== undefined;

  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom < SCROLL_THRESHOLD) {
      onLoadMore();
    }
  }, [onLoadMore, hasNextPage, isFetchingNextPage]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: calculatedHeight }}
        data-testid="multi-select-table-loading"
      >
        <Spinner size="xl" />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className="border-chariot-stroke flex items-center justify-center rounded-md border"
        style={{ height: calculatedHeight }}
        data-testid="multi-select-table-empty"
      >
        <span className="text-chariot-text-secondary text-sm">
          {emptyMessage}
        </span>
      </div>
    );
  }

  const renderRow = (
    row: (typeof rows)[0],
    style?: CSSProperties,
    showTopBorder = true
  ) => {
    const isSelected = selectedKeys.has(row.original.key);

    const handleRowClick = () => {
      const next = new Set(selectedKeys);
      if (isSelected) {
        next.delete(row.original.key);
      } else {
        next.add(row.original.key);
      }
      onSelectionChange(next);
    };

    return (
      <tr
        key={row.id}
        data-testid={`multi-select-table-row-${row.id}`}
        onClick={handleRowClick}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        style={style}
        tabIndex={0}
        role="row"
        aria-selected={isSelected}
        className={cn(
          'cursor-pointer transition-colors duration-150',
          'hover:bg-chariot-background-secondary',
          'focus:ring-chariot-brand-primary outline-hidden focus:ring-2 focus:ring-inset',
          showTopBorder && 'border-chariot-stroke border-t',
          isSelected && 'bg-chariot-background-secondary'
        )}
      >
        {row.getVisibleCells().map(cell => (
          <td key={cell.id} className="px-3 py-1">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="flex flex-col">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        data-testid="multi-select-table-scroll-container"
        className="border-chariot-stroke bg-chariot-background-primary overflow-auto rounded-md border"
        style={{ height: calculatedHeight }}
      >
        <table className="w-full table-fixed text-sm" role="table">
          <colgroup>
            {table.getAllColumns().map(column => {
              const size = column.getSize();
              const isFlexible =
                (column.columnDef.meta as { flexible?: boolean })?.flexible ??
                false;
              return (
                <col
                  key={column.id}
                  style={
                    isFlexible
                      ? undefined
                      : { width: size, minWidth: size, maxWidth: size }
                  }
                />
              );
            })}
          </colgroup>

          <thead
            role="rowgroup"
            className="bg-chariot-background-secondary sticky top-0 z-10"
          >
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      aria-sort={
                        header.column.getIsSorted() === 'asc'
                          ? 'ascending'
                          : header.column.getIsSorted() === 'desc'
                            ? 'descending'
                            : 'none'
                      }
                      className={cn(
                        'text-chariot-text-secondary px-3 py-2 text-left text-xs font-medium uppercase',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {isSorted === 'asc' && (
                          <ChevronUpIcon className="size-3" />
                        )}
                        {isSorted === 'desc' && (
                          <ChevronDownIcon className="size-3" />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody role="rowgroup">
            {useVirtualization ? (
              <>
                {virtualRows.length > 0 && virtualRows[0].start > 0 && (
                  <tr
                    style={{ height: virtualRows[0].start }}
                    aria-hidden="true"
                  >
                    <td colSpan={allColumns.length} />
                  </tr>
                )}
                {virtualRows.map((virtualRow, index) => {
                  const row = rows[virtualRow.index];
                  const isFirstVisible =
                    index === 0 && virtualRows[0].start === 0;
                  return renderRow(row, { height: rowHeight }, !isFirstVisible);
                })}
                {(() => {
                  const bottomHeight =
                    totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0);
                  return (
                    virtualRows.length > 0 &&
                    bottomHeight > 0 && (
                      <tr style={{ height: bottomHeight }} aria-hidden="true">
                        <td colSpan={allColumns.length} />
                      </tr>
                    )
                  );
                })()}
              </>
            ) : (
              rows.map((row, index) => renderRow(row, undefined, index > 0))
            )}
          </tbody>
        </table>
      </div>

      {hasPaginationSupport && (
        <InfiniteScrollIndicator
          variant="relative"
          isLoading={isFetchingNextPage}
          hasMore={hasNextPage === true}
          itemCount={data.length}
          minItemsThreshold={10}
        />
      )}
    </div>
  );
}
